import { useEffect, useRef, useState } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { makeZodI18nMap } from 'zod-i18n-map'
import { useQuery } from '@tanstack/react-query'
import { HookFormSelect, HookFormTextField } from '@portal/pkg-hook-form'
import { DashboardSubType, DashboardType } from '@portal/pkg-api/gen/admin-console'
import type {
  CreateDashboardRequest,
  DashboardDetailResponse,
  UpdateDashboardRequest,
} from '@portal/pkg-api/gen/admin-console'
import { FilterCriteria } from '@portal/pkg-filter-criteria'
import { DashboardQueries } from '../queries'
import { useCreateDashboard, useUpdateDashboard } from '../mutations'
import {
  DashboardSchema,
  dashboardDefaults,
  type DashboardFormValues,
} from '../model/dashboardSchema'
import { toCriteriaRows, toFilterRestrictions } from '../model/filterRestrictions'
import { TypeBadge } from './TypeBadge'

const DRAWER_WIDTH = '40vw'

const TYPE_OPTIONS = [
  DashboardType.HOMEPAGE,
  DashboardType.SUMMARY,
  DashboardType.INSIGHTS,
] as const

const SUB_TYPE_OPTIONS = [DashboardSubType.REPORT, DashboardSubType.PAGINATED] as const

function toFormValues(dashboard: DashboardDetailResponse): DashboardFormValues {
  return {
    name: dashboard.name,
    type: dashboard.type,
    reportId: dashboard.reportId ?? '',
    subType: dashboard.subType ?? '',
    datasetId: dashboard.datasetId ?? '',
    entityTypeId: dashboard.entityType ?? '',
    roleIds: dashboard.roleIds ?? [],
    criteria: toCriteriaRows(dashboard.filterRestrictions),
  }
}

/**
 * Builds the payload for `values.type` alone. Fields belonging to the other types are left off
 * entirely rather than sent null: the server answers 400 for a field that does not apply, so a
 * user who fills in Insights fields and then switches to Homepage must not have them submitted.
 */
function toRequest(values: DashboardFormValues): CreateDashboardRequest {
  const base: CreateDashboardRequest = {
    type: values.type,
    name: values.name.trim(),
    reportId: values.reportId.trim(),
    roleIds: values.roleIds,
  }
  if (values.type === DashboardType.INSIGHTS) {
    return {
      ...base,
      subType: values.subType === '' ? null : values.subType,
      datasetId: values.datasetId.trim() || null,
    }
  }
  if (values.type === DashboardType.SUMMARY) {
    return {
      ...base,
      entityType: values.entityTypeId === '' ? null : values.entityTypeId,
      filterRestrictions: toFilterRestrictions(values.criteria),
    }
  }
  return base
}

interface DashboardDrawerProps {
  /** Omit to create. Supplying one loads that dashboard and locks the type. */
  dashboardId?: number
  onClose: () => void
}

export function DashboardDrawer({ dashboardId, onClose }: DashboardDrawerProps) {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()
  const isEdit = dashboardId !== undefined
  const createDashboard = useCreateDashboard()
  const updateDashboard = useUpdateDashboard()

  const { data: roles } = useQuery(DashboardQueries.roles())
  const { data: entityTypes } = useQuery(DashboardQueries.entityTypes())
  const { data: dashboard } = useQuery({
    ...DashboardQueries.detail(dashboardId ?? 0),
    enabled: isEdit,
  })

  const form = useForm<DashboardFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(DashboardSchema, { errorMap: makeZodI18nMap({ handlePath: false }) }),
    defaultValues: dashboardDefaults,
  })
  const { control, handleSubmit, reset, setValue } = form

  const type = useWatch({ control, name: 'type' })
  const entityTypeId = useWatch({ control, name: 'entityTypeId' })

  // Hovering over the title shows the dashboard's ID, for the few who need it, and the name too
  // when the Name field cuts it off. Measured as the tooltip opens, so it follows any edit.
  const nameInputRef = useRef<HTMLInputElement>(null)
  const [cutOffName, setCutOffName] = useState<string>()
  const measureName = () => {
    const input = nameInputRef.current
    setCutOffName(input && input.scrollWidth > input.clientWidth ? input.value : undefined)
  }

  // Detail arrives after the drawer opens, so seed the form once it lands.
  useEffect(() => {
    if (dashboard) reset(toFormValues(dashboard))
  }, [dashboard, reset])

  const onSubmit = (values: DashboardFormValues) => {
    const onError = () => enqueueSnackbar(t('dashboards.form.saveFailed'), { variant: 'error' })

    if (isEdit) {
      const { type: _omitted, ...request } = toRequest(values)
      updateDashboard.mutate(
        { id: dashboardId, ...(request as UpdateDashboardRequest) },
        {
          onSuccess: () => {
            enqueueSnackbar(t('dashboards.form.dashboardUpdated'), { variant: 'success' })
            onClose()
          },
          onError,
        },
      )
      return
    }
    createDashboard.mutate(toRequest(values), {
      onSuccess: () => {
        enqueueSnackbar(t('dashboards.form.dashboardAdded'), { variant: 'success' })
        onClose()
      },
      onError,
    })
  }

  const isSaving = createDashboard.isPending || updateDashboard.isPending

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
          }}
        >
          <Tooltip
            title={
              dashboard ? (
                <>
                  {cutOffName !== undefined && (
                    <>
                      {cutOffName}
                      <br />
                    </>
                  )}
                  {t('dashboards.table.id', { id: dashboard.id })}
                </>
              ) : (
                ''
              )
            }
            describeChild
            disableInteractive
            onOpen={measureName}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t(isEdit ? 'dashboards.drawer.editTitle' : 'dashboards.drawer.addTitle')}
            </Typography>
          </Tooltip>
          <Stack direction="row" spacing={1}>
            <Button color="secondary" onClick={onClose}>
              {t('dashboards.drawer.cancel')}
            </Button>
            <Button
              variant="contained"
              disabled={isSaving}
              onClick={() => void handleSubmit(onSubmit)()}
            >
              {t('dashboards.drawer.save')}
            </Button>
          </Stack>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <FormProvider {...form}>
            <Stack spacing={2}>
              <HookFormTextField<DashboardFormValues>
                name="name"
                control={control}
                label={t('dashboards.form.name')}
                variant="outlined"
                size="small"
                fullWidth
                inputRef={nameInputRef}
              />
              {isEdit ? (
                // The type is immutable once created -- the server rejects a change -- so show it
                // as the same badge the grid uses rather than a control that implies otherwise.
                <Stack spacing={0.5}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('dashboards.form.type')}
                  </Typography>
                  <Box>
                    <TypeBadge type={type} subType={dashboard?.subType} />
                  </Box>
                </Stack>
              ) : (
                <HookFormSelect<DashboardFormValues>
                  name="type"
                  control={control}
                  label={t('dashboards.form.type')}
                  labelId="dashboard-type-label"
                  slotProps={{ inputLabel: { id: 'dashboard-type-label' } }}
                  size="small"
                  fullWidth
                >
                  {TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {t(`dashboards.table.type.${option}`)}
                    </MenuItem>
                  ))}
                </HookFormSelect>
              )}

              {type === DashboardType.INSIGHTS && (
                <HookFormSelect<DashboardFormValues>
                  name="subType"
                  control={control}
                  label={t('dashboards.form.subType')}
                  labelId="dashboard-sub-type-label"
                  slotProps={{ inputLabel: { id: 'dashboard-sub-type-label' } }}
                  size="small"
                  fullWidth
                >
                  {SUB_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {t(`dashboards.table.subType.${option}`)}
                    </MenuItem>
                  ))}
                </HookFormSelect>
              )}

              <HookFormTextField<DashboardFormValues>
                name="reportId"
                control={control}
                label={t('dashboards.form.reportId')}
                variant="outlined"
                size="small"
                fullWidth
              />

              {type === DashboardType.INSIGHTS && (
                <HookFormTextField<DashboardFormValues>
                  name="datasetId"
                  control={control}
                  label={t('dashboards.form.datasetId')}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              )}

              {type === DashboardType.SUMMARY && (
                <Controller
                  control={control}
                  name="entityTypeId"
                  render={({ field, fieldState: { error } }) => (
                    <FormControl fullWidth size="small" error={!!error}>
                      <InputLabel id="dashboard-entity-type-label">
                        {t('dashboards.form.entityType')}
                      </InputLabel>
                      <Select
                        labelId="dashboard-entity-type-label"
                        label={t('dashboards.form.entityType')}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(event) => {
                          field.onChange(event.target.value)
                          // A field belongs to one entity type, so rows built against the
                          // previous one would submit ids the server rejects. Done here rather
                          // than in an effect so that loading an existing dashboard -- which also
                          // changes this value -- does not wipe the criteria being edited.
                          setValue('criteria', [], { shouldDirty: true })
                        }}
                      >
                        {(entityTypes ?? []).map((entityType) => (
                          <MenuItem key={entityType.id} value={entityType.id}>
                            {entityType.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && <FormHelperText>{error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              )}

              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                {t('dashboards.form.accessSection')}
              </Typography>
              <HookFormSelect<DashboardFormValues>
                name="roleIds"
                control={control}
                label={t('dashboards.form.roles')}
                labelId="dashboard-roles-label"
                slotProps={{ inputLabel: { id: 'dashboard-roles-label' } }}
                size="small"
                fullWidth
                multiple
                renderValue={(selected) =>
                  (selected as number[])
                    .map((id) => (roles ?? []).find((role) => role.id === id)?.name)
                    .filter(Boolean)
                    .join(', ')
                }
              >
                {(roles ?? []).map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </HookFormSelect>

              {type === DashboardType.SUMMARY && (
                <FilterCriteria
                  name="criteria"
                  label={t('dashboards.form.criteria.title')}
                  entityTypeId={entityTypeId === '' ? undefined : entityTypeId}
                />
              )}
            </Stack>
          </FormProvider>
        </Box>
      </Stack>
    </Drawer>
  )
}
